import React, { useEffect, useRef } from 'react';
import { ILxsCreateOrderResources } from '../lxs-create-order.props.autogenerated';
import { ILxsOrderData } from '../lxs-create-order.data';
import AlertModal from './alert-modal';
import { ISoldToTableData } from '../../../actions/get-sold-tos.action';
import { IShipToTableData } from '../../../actions/get-ship-tos.action';

const useStepNavigation = (
    step: number,
    resources: ILxsCreateOrderResources,
    context: any,
    orderData: ILxsOrderData,
    setGlobalFilter: React.Dispatch<React.SetStateAction<string>>,
    setOpen: (data: any, onClose?: () => void) => void,
    setClose: () => void,
    setStep: React.Dispatch<React.SetStateAction<number>>,
    setSoldTo: (data: ISoldToTableData) => void,
    setShipTo: (data: IShipToTableData) => void,
    pushHistoryState: (step: number) => void
) => {
    const previousStepRef = useRef(step);

    useEffect(() => {
        setGlobalFilter('');

        if (step > previousStepRef.current || !window.location.hash.includes('step')) {
            pushHistoryState(step);
        }

        previousStepRef.current = step;

        const handlePopState = (event: any) => {
            const currentPageIndex = getCurrentPageIndex();
            const samePageIndex = currentPageIndex - 1;

            if (currentPageIndex > previousStepRef.current) {
                event.preventDefault();
                showAlertModal(resources.creatOrderPreventForwardHint, resources.createOrderPreventForwardHintConfirm, samePageIndex);
                pushHistoryState(samePageIndex);
            } else if (currentPageIndex === previousStepRef.current) {
                handleSamePageIndex(currentPageIndex, samePageIndex);
            } else {
                handleNewStep(currentPageIndex);
            }
        };

        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [step, setGlobalFilter, setStep, setOpen, setClose, resources, context, orderData]);

    const getCurrentPageIndex = () => {
        const currentPageHash = window.location.hash;
        return Number(currentPageHash.substring(currentPageHash.length - 1));
    };

    const showAlertModal = (message: string, confirm: string, samePageIndex: number) => {
        setOpen(
            {
                content: (
                    <AlertModal
                        message={message}
                        confirm={confirm}
                        onClick={() => {
                            window.history.replaceState({ samePageIndex }, '', `#step-${samePageIndex}`);
                            setShipTo({} as IShipToTableData);
                            setStep?.(samePageIndex);
                            setClose();
                        }}
                    />
                ),
                size: 'sm'
            },
            () => {
                pushHistoryState(step);
            }
        );
    };

    const handleSamePageIndex = (currentPageIndex: number, samePageIndex: number) => {
        if (samePageIndex < 0) {
            window.location.href = context.app.routes.home.destinationUrl;
        } else {
            window.history.replaceState({ samePageIndex }, '', `#step-${samePageIndex}`);
            setStep?.(samePageIndex);
        }
    };

    const handleNewStep = (newStep: number) => {
        if (step === 1) {
            setStep?.(newStep);
            window.location.href = context.app.routes.home.destinationUrl;
        } else {
            switch (newStep) {
                case 1:
                    setSoldTo({} as ISoldToTableData);
                    window.history.replaceState({ newStep }, '', `#step-${newStep}`);
                    setStep(newStep);
                    break;
                case 2:
                    if (orderData.products !== undefined) {
                        window.history.replaceState({ newStep }, '', `#step-${previousStepRef.current}`);
                        showAlertModal(resources.createOrderDeleteProductsHint, resources.createOrderDeleteProductsHintConfirm, newStep);
                    } else {
                        setStep?.(newStep);
                    }
                    break;
                default:
                    setStep?.(newStep);
                    break;
            }
        }
    };
};

export default useStepNavigation;
